/**
 * Notices that happened ON THIS DEVICE — today, "payslip downloaded" — shown
 * in the employee bell next to the server-backed leave decisions
 * (`use_employee_notifications.ts`).
 *
 * Why on-device rather than a server row: the event is local. The file only
 * exists on this phone, and the notice's one action is "open that file", so a
 * copy on the employer's tenant DO would point every other device at nothing.
 * It is the fallback for the OS notification `my_pay_download.ts` raises:
 * recorded only when that one was not handed to the OS (no notification
 * API, permission denied, schedule failed), so one download never produces
 * two "Payslip downloaded" notices. Suppression after the OS accepted it
 * (channel turned off, focus mode) is not detectable, so no entry then.
 * It stays in the app until the employee taps it.
 *
 * ── Whose notices these are ────────────────────────────────────────────────
 * A phone can be shared, and each entry opens someone's salary PDF in one
 * tap. So the list belongs to exactly one user: `claim_device_notifications`
 * (called by `AuthProvider` whenever the signed-in user resolves) wipes it
 * when a DIFFERENT user signs in — which also covers a session that expired
 * without an explicit sign-out — and sign-out clears it outright.
 *
 * Stored in `localStorage` under a non-credential key: a user id, file URIs
 * and pay periods, nothing that authenticates anyone (docs/security-rules.md).
 * Every access is guarded — storage can be unavailable or hold a malformed
 * blob, and the bell renders in the chrome of every employee screen, so a
 * throw here would take the whole app down.
 */
import * as React from 'react';
import type { PayPeriod } from './types.js';

export interface DeviceNotification {
  id: string;
  type: 'payslip_downloaded';
  created_at: string;
  /** Which employer the payslip is from — an employee can have several. */
  entity_id: string;
  /** Local file URI of the saved payslip. */
  uri: string;
  mime_type: string;
  period: PayPeriod;
}

export type NewDeviceNotification = Omit<
  DeviceNotification,
  'id' | 'created_at'
>;

interface StoredState {
  /** The user these notices belong to; `null` until the first claim. */
  owner_user_id: string | null;
  items: readonly DeviceNotification[];
}

const STORAGE_KEY = 'wagepro-device-notifications';
/** Oldest entries drop off beyond this, so the list cannot grow forever. */
export const MAX_DEVICE_NOTIFICATIONS = 20;

const EMPTY_STATE: StoredState = { owner_user_id: null, items: [] };
let cache: StoredState | null = null;
const listeners = new Set<() => void>();

function is_record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function is_device_notification(value: unknown): value is DeviceNotification {
  if (!is_record(value) || !is_record(value.period)) return false;
  return (
    typeof value.id === 'string' &&
    value.type === 'payslip_downloaded' &&
    typeof value.created_at === 'string' &&
    typeof value.entity_id === 'string' &&
    typeof value.uri === 'string' &&
    typeof value.mime_type === 'string' &&
    typeof value.period.year === 'number' &&
    typeof value.period.month === 'number'
  );
}

function load(): StoredState {
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return EMPTY_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (!is_record(parsed) || !Array.isArray(parsed.items)) return EMPTY_STATE;
    return {
      owner_user_id:
        typeof parsed.owner_user_id === 'string' ? parsed.owner_user_id : null,
      items: parsed.items.filter(is_device_notification),
    };
  } catch (err) {
    console.error('[wagepro-web] reading device notifications failed', err);
    return EMPTY_STATE;
  }
}

function state(): StoredState {
  cache ??= load();
  return cache;
}

function save(next: StoredState): void {
  cache = next;
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (err) {
    // The in-memory list still updates, so the bell stays correct until the
    // app is closed even when it cannot be persisted.
    console.error('[wagepro-web] saving device notifications failed', err);
  }
  for (const listener of listeners) listener();
}

function set_items(items: readonly DeviceNotification[]): void {
  save({ ...state(), items });
}

/** Newest first. */
export function get_device_notifications(): readonly DeviceNotification[] {
  return state().items;
}

/**
 * Bind the list to `user_id`. A different user than the stored owner wipes
 * it first, so nobody signing in on a shared phone inherits the previous
 * user's payslip links.
 */
export function claim_device_notifications(user_id: string): void {
  if (state().owner_user_id === user_id) return;
  save({ owner_user_id: user_id, items: [] });
}

/** Sign-out: drop every notice and the owner. */
export function clear_device_notifications(): void {
  save(EMPTY_STATE);
}

/**
 * Add a notice. Downloading the same payslip again REPLACES its entry rather
 * than stacking a second one for the same employer and period.
 */
export function record_device_notification(
  entry: NewDeviceNotification,
  now: Date = new Date(),
): DeviceNotification {
  const notice: DeviceNotification = {
    ...entry,
    id: `device-${String(now.getTime())}-${Math.random().toString(36).slice(2, 8)}`,
    created_at: now.toISOString(),
  };
  const others = get_device_notifications().filter(
    (n) =>
      !(
        n.entity_id === entry.entity_id &&
        n.period.year === entry.period.year &&
        n.period.month === entry.period.month
      ),
  );
  set_items([notice, ...others].slice(0, MAX_DEVICE_NOTIFICATIONS));
  return notice;
}

export function dismiss_device_notification(id: string): void {
  set_items(get_device_notifications().filter((n) => n.id !== id));
}

/** The OS notification for a file was tapped — its bell entry is done too. */
export function dismiss_device_notifications_for_uri(uri: string): void {
  const items = get_device_notifications();
  if (!items.some((n) => n.uri === uri)) return;
  set_items(items.filter((n) => n.uri !== uri));
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useDeviceNotifications(): readonly DeviceNotification[] {
  return React.useSyncExternalStore(
    subscribe,
    get_device_notifications,
    get_device_notifications,
  );
}

/** Test seam: forget the in-memory copy so the next read goes to storage. */
export function reset_device_notifications_cache(): void {
  cache = null;
}
